import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Upload } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import FileUploadField from './FileUploadField';
import HelpTip from './HelpTip';
import FilterToolbar, { FilterForm } from './FilterToolbar';

export default function IntegrationErrors() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');

  const uploadAndParse = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('errorWorkbook', selectedFile);
      const base = getApiBase();
      const res = await fetch(`${base}/api/errors/parse`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to parse workbook');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const rows = result?.errors || [];
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? rows.filter((r) =>
        [
          r.personNumber,
          r.actionType,
          r.actionTypeDescription,
          r.sapRecordType,
          r.decodedSummary,
          r.errorMessage,
          r.recType,
          r.wageType,
          r.status,
          r.sheet,
        ]
          .map((v) => String(v ?? '').toLowerCase())
          .some((v) => v.includes(needle))
      )
    : rows;

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Integration</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Errors</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Upload the ADP transformation error workbook (all tabs). Decodes GlobalView and OGPI
          error codes, SAP infotypes, and wage types.{' '}
          <HelpTip term="ogpi" />
        </p>
      </header>

      <div className="card-panel p-5">
        <FileUploadField
          label="Error workbook"
          hint="Multiple tabs supported — parses on upload"
          accept=".xlsx,.xls"
          file={file}
          onChange={(selected) => uploadAndParse(selected)}
          helpTerm="exception"
          HelpTipComponent={HelpTip}
        />
        {loading && (
          <p className="mt-3 flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Parsing workbook…
          </p>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}

      {result && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-warning inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {result.errorCount.toLocaleString()} error row{result.errorCount === 1 ? '' : 's'}
            </span>
            <span className="badge-neutral">{result.sheetCount} tab(s)</span>
            {result.fileName && <span className="badge-neutral truncate max-w-xs">{result.fileName}</span>}
          </div>

          {result.sheets?.length > 0 && (
            <div className="card-panel p-4 text-xs text-slate-400">
              <p className="mb-2 font-medium text-slate-300">Tabs scanned</p>
              <ul className="space-y-1">
                {result.sheets.map((s) => (
                  <li key={s.name}>
                    <span className="font-mono text-slate-200">{s.name}</span>
                    — {s.errorCount} error{s.errorCount === 1 ? '' : 's'}
                    {s.meta?.columnLabels?.message && (
                      <span className="text-slate-500">
                        {' '}
                        · columns: {s.meta.columnLabels.personNumber || '—'} /{' '}
                        {s.meta.columnLabels.message}
                      </span>
                    )}
                    {s.skipped?.noHeader && ' (no error headers found)'}
                    {s.skipped?.missingColumns && ' (no message / person columns found)'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <FilterForm onApply={() => setFilter(filterDraft)}>
              <FilterToolbar
                onClear={() => {
                  setFilterDraft('');
                  setFilter('');
                }}
                hasActiveFilters={!!filter.trim()}
              >
                <input
                  value={filterDraft}
                  onChange={(e) => setFilterDraft(e.target.value)}
                  placeholder="Person, action, SAP type, message…"
                  className="filter-input min-w-[16rem] text-sm"
                />
              </FilterToolbar>
            </FilterForm>
            <p className="text-xs text-slate-500">
              Showing {visible.length.toLocaleString()} of {rows.length.toLocaleString()}
            </p>
          </div>

          <div className="card-panel overflow-x-auto">
            <table className="data-table text-sm">
              <thead>
                <tr>
                  <th className="px-2 py-2 font-medium">Person</th>
                  <th className="px-2 py-2 font-medium">What it means</th>
                  <th className="px-2 py-2 font-medium">Action</th>
                  <th className="px-2 py-2 font-medium">SAP type</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Raw message</th>
                  <th className="px-2 py-2 font-medium">Tab</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-2 py-10 text-center text-slate-400">
                      {rows.length === 0
                        ? 'No error rows with message text found. Check that the sheet has Message Text (or similar) and person columns such as Prev. Personnel No.'
                        : 'No rows match the filter.'}
                    </td>
                  </tr>
                ) : (
                  visible.map((row, i) => (
                    <tr key={`${row.sheet}-${i}-${row.personNumber}-${row.recType}`}>
                      <td className="px-2 py-2 font-mono text-slate-200">{row.personNumber || '—'}</td>
                      <td className="max-w-sm px-2 py-2 text-cyan-100/90">
                        {row.decodedSummary ? (
                          <span>{row.decodedSummary}</span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                        {row.globalViewMessage?.code && (
                          <div className="mt-1">
                            <span
                              className="rounded bg-amber-950/80 px-1.5 py-0.5 font-mono text-[10px] text-amber-300/90"
                              title={row.globalViewMessage.msgClassDescription}
                            >
                              GV {row.globalViewMessage.code}
                            </span>
                          </div>
                        )}
                        {row.infotypes?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {row.infotypes.map((it) => (
                              <span
                                key={it.pCode || it.code}
                                className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-400"
                                title={it.hint}
                              >
                                {it.pCode || it.code}
                              </span>
                            ))}
                          </div>
                        )}
                        {row.wageTypes?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {row.wageTypes.map((wt) => (
                              <span
                                key={wt.code}
                                className="rounded bg-cyan-950 px-1.5 py-0.5 font-mono text-[10px] text-cyan-400/90"
                                title={wt.oracleElementName || wt.longText}
                              >
                                {wt.code}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="max-w-[8rem] px-2 py-2 text-slate-300">
                        <span className="font-mono">{row.actionType || '—'}</span>
                        {row.actionTypeDescription && (
                          <span className="block truncate text-xs text-slate-500" title={row.actionTypeDescription}>
                            {row.actionTypeDescription}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono text-slate-400">
                        {row.sapRecordType || row.infotypes?.[0]?.code || row.recType || '—'}
                      </td>
                      <td className="px-2 py-2 font-mono text-amber-400/90">{row.status}</td>
                      <td
                        className="max-w-xs px-2 py-2 text-xs text-slate-500"
                        title={row.messageRaw}
                      >
                        {row.errorMessage}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500">{row.sheet}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!result && !loading && (
        <div className="card-panel flex flex-col items-center px-6 py-14 text-center">
          <Upload className="h-10 w-10 text-slate-600" />
          <p className="mt-4 text-sm text-slate-400">
            Upload a transformation error export to review failed interface records.
          </p>
        </div>
      )}
    </div>
  );
}
